import { GraphQLFormattedError } from "graphql";
import { ApolloError } from "@apollo/client";
import { toast } from "sonner";
import { logMobileNetworkError } from "./mobile-debug";

export interface ErrorInfo {
  message: string;
  code?: string;
  field?: string;
  type: "graphql" | "network" | "validation";
}

export class ErrorHandler {
  static handleApolloError(error: ApolloError): void {
    // Handle GraphQL errors
    if (error.graphQLErrors?.length > 0) {
      error.graphQLErrors.forEach(gqlError => {
        this.handleGraphQLError(gqlError);
      });
    }

    // Handle network errors
    if (error.networkError) {
      this.handleNetworkError(error.networkError);
    }

    // Handle general Apollo errors (if no specific errors)
    if (!error.graphQLErrors?.length && !error.networkError) {
      this.showErrorToast({
        message: error.message || "An unexpected error occurred",
        type: "graphql",
      });
    }
  }

  static handleGraphQLError(error: GraphQLFormattedError): void {
    const extensions = error.extensions || {};
    const code = extensions.code as string;

    // Create error info
    const errorInfo: ErrorInfo = {
      message: error.message,
      code,
      type: "graphql",
    };

    // Handle specific error codes
    switch (code) {
      case "UNAUTHENTICATED":
        this.showErrorToast({
          ...errorInfo,
          message: "Please log in to continue",
        });
        // Could also redirect to login here
        break;

      case "FORBIDDEN":
        this.showErrorToast({
          ...errorInfo,
          message: "You don't have permission to perform this action",
        });
        break;

      case "VALIDATION_ERROR":
        this.showErrorToast({
          ...errorInfo,
          message: `Validation error: ${error.message}`,
        });
        break;

      case "USER_NOT_FOUND":
        this.showErrorToast({
          ...errorInfo,
          message: "User not found",
        });
        break;

      case "EMAIL_SEND_ERROR":
        this.showErrorToast({
          ...errorInfo,
          message: "Failed to send email. Please try again.",
        });
        break;

      case "INVALID_CREDENTIALS":
        this.showErrorToast({
          ...errorInfo,
          message: "Invalid email or password",
        });
        break;

      case "TOKEN_EXPIRED":
        this.showErrorToast({
          ...errorInfo,
          message: "Your session has expired. Please log in again.",
        });
        break;

      default:
        // Show the original error message for unhandled codes
        this.showErrorToast(errorInfo);
        break;
    }
  }

  static handleNetworkError(error: Error & { statusCode?: number }): void {
    const statusCode = error.statusCode;
    let message = "Network error occurred";

    // Log mobile-specific network errors
    logMobileNetworkError(error, `Network error with status: ${statusCode}`);

    switch (statusCode) {
      case 400:
        message = "Bad request - please check your input";
        break;
      case 401:
        message = "Authentication required";
        break;
      case 403:
        message = "Access forbidden";
        break;
      case 404:
        message = "Service not found";
        break;
      case 500:
        message = "Server error - please try again later";
        break;
      case 503:
        message = "Service temporarily unavailable";
        break;
      default:
        message =
          error.message || "Connection failed - please check your internet";
        break;
    }

    this.showErrorToast({
      message,
      type: "network",
    });
  }

  static showErrorToast(errorInfo: ErrorInfo): void {
    toast.error(errorInfo.message);
  }

  static showSuccessToast(message: string): void {
    toast.success(message);
  }

  static showInfoToast(message: string): void {
    toast.info(message);
  }
}
